import { chakra, useMultiStyleConfig } from '@chakra-ui/system';
import type { HTMLChakraProps } from '@chakra-ui/system';
import { cx } from '@chakra-ui/utils';

import { Badge, getTextContainsHighlight } from '@frontend/components';

import {
  applyHighlighting,
  getHighlightQueryStringForConstituent,
  usePartContext,
} from './util';

export type PartLabelProps = HTMLChakraProps<'span'>;

export const PartLabel = ({ className, ...props }: PartLabelProps) => {
  const { number, filename, rev, isItar, highlight, size, variant } =
    usePartContext();
  const styles = useMultiStyleConfig('Part', { size, variant });

  const highlightedNumber = applyHighlighting({
    contentToHighlight: number,
    constituent: 'number',
    highlight,
  });
  const highlightedFilename = applyHighlighting({
    contentToHighlight: filename,
    constituent: 'filename',
    highlight,
  });
  const highlightedRev = applyHighlighting({
    contentToHighlight: rev,
    constituent: 'rev',
    highlight,
  });

  const filenameHighlightQueryString = getHighlightQueryStringForConstituent({
    constituent: 'filename',
    highlight,
  });

  const filenameIsHighlighted =
    !!filename &&
    !!filenameHighlightQueryString &&
    getTextContainsHighlight({
      text: filename,
      query: [filenameHighlightQueryString],
    });

  const _className = cx('part__label-container', className);

  return (
    <chakra.span
      __css={styles.labelContainer}
      {...props}
      className={_className}
    >
      {/* Display the part number if available, use filename as a fallback */}
      {highlightedNumber || highlightedFilename ? (
        <chakra.span className="part__label" __css={styles.label}>
          {highlightedNumber || highlightedFilename}
        </chakra.span>
      ) : (
        <chakra.span
          className="part__label-placeholder"
          __css={styles.labelPlaceholder}
        >
          --
        </chakra.span>
      )}
      {rev && (
        <chakra.span className="part__label-group" __css={styles.labelGroup}>
          <chakra.span
            className="part__label-prefix"
            __css={styles.labelPrefix}
          >
            rev
          </chakra.span>
          <chakra.span className="part__label" __css={styles.label}>
            {highlightedRev}
          </chakra.span>
        </chakra.span>
      )}
      {/* If the filename is highlighted and was not displayed above, display it
          here to ensure all highlighted text is displayed */}
      {highlightedNumber && highlightedFilename && filenameIsHighlighted && (
        <chakra.div
          className="part__secondary-label"
          __css={styles.secondaryLabel}
        >
          {highlightedFilename}
        </chakra.div>
      )}
      {isItar && (
        <chakra.div
          className="part__badge-container"
          __css={styles.badgeContainer}
        >
          <Badge
            size={size === 'xl' ? 'sm' : 'xs'}
            bg="tag.ITAR"
            borderColor="tag.ITAR"
            color="white"
          >
            ITAR
          </Badge>
        </chakra.div>
      )}
    </chakra.span>
  );
};
